/**
 * BotGame - Partida X01 contra la IA
 * 501 Dart League Web
 * 
 * Basado en X01Game pero con el turno del bot automatizado:
 * - Player 0 = Tú (input manual con numpad)
 * - Player 1 = Bot (turno automático con delay de "pensamiento")
 * 
 * El bot genera puntuaciones realistas vía botEngine.js.
 * Todo se ejecuta en el cliente, sin servidor.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import { isValidMove } from './utils/gameUtils';
import { generateBotScore, generateClosingDarts, getBotThinkingTime, BOT_LEVELS } from './utils/botEngine';
import TrainingMatchResultModal from './TrainingMatchResultModal';
import ScoreCelebrationBanner from './ScoreCelebrationBanner';
import {
  createEmptyMatchAdvancedStats,
  normalizeMatchAdvancedStats,
  registerAdvancedTurn,
} from './utils/leagueMatchStats';
import {
  getScoreCelebrationType,
  SCORE_CELEBRATION_DURATION_MS,
  shouldDelayResultModalForCelebration,
} from './utils/scoreCelebration';
import './styles/index.css';
import './styles/x01game.css';
import SponsorsStrip from './SponsorsStrip';

const EMPTY_RESULT_MODAL = {
  visible: false,
  data: null,
  pending: false,
};

// ========== MODALES ==========

const DardosCierreModal = ({ visible, closingScore, onSelect }) => {
  if (!visible) return null;
  // 1 dardo: Bull (50) o D1-D20 (2-40 pares). D21+ NO existe en la diana.
  const canOneD = closingScore === 50 || (closingScore <= 40 && closingScore >= 2 && closingScore % 2 === 0);
  const canTwoD = closingScore <= 110;

  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-dardos-modal">
        <h2>¡Has cerrado!</h2>
        <p className="x01-modal-subtitle">
          Cerraste <strong>{closingScore}</strong>. ¿Con cuántos dardos?
        </p>
        <div className="x01-dardos-options">
          <button className={`x01-dardo-btn ${!canOneD ? 'disabled' : ''}`} onClick={() => canOneD && onSelect(1)} disabled={!canOneD}>
            <span className="x01-dardo-num">1</span>
            <span className="x01-dardo-label">dardo</span>
            {!canOneD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button className={`x01-dardo-btn ${!canTwoD ? 'disabled' : ''}`} onClick={() => canTwoD && onSelect(2)} disabled={!canTwoD}>
            <span className="x01-dardo-num">2</span>
            <span className="x01-dardo-label">dardos</span>
            {!canTwoD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button className="x01-dardo-btn" onClick={() => onSelect(3)}>
            <span className="x01-dardo-num">3</span>
            <span className="x01-dardo-label">dardos</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ visible, title, message, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-confirm-modal">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="x01-confirm-actions">
          <button className="x01-result-btn secondary" onClick={onCancel}>Cancelar</button>
          <button className="x01-result-btn primary" onClick={onConfirm}>Confirmar</button>
        </div>
      </div>
    </div>
  );
};

// ========== COMPONENTE PRINCIPAL ==========

const BotGame = ({ config, onBack }) => {
  const { gameMode, legsToWin, player1Name, player2Name, botLevel } = config;
  const playerNames = useMemo(() => [player1Name, player2Name], [player1Name, player2Name]);

  const botProfile = BOT_LEVELS[botLevel] || BOT_LEVELS[3];

  // Estado del juego
  const [scores, setScores] = useState(() => [gameMode, gameMode]);
  const [matchesWon, setMatchesWon] = useState(() => [0, 0]);
  const [activePlayer, setActivePlayer] = useState(0);
  const [input, setInput] = useState('');
  const [dartsThrown, setDartsThrown] = useState(() => [0, 0]);
  const [throws, setThrows] = useState(() => [[], []]);
  const [count180s, setCount180s] = useState(() => [0, 0]);
  const [moveHistory, setMoveHistory] = useState([]);
  const [matchCount, setMatchCount] = useState(0);
  const [firstStarter, setFirstStarter] = useState(0);
  const [advancedStats, setAdvancedStats] = useState(() => createEmptyMatchAdvancedStats());
  const [scoreCelebration, setScoreCelebration] = useState(null);
  const [botThinking, setBotThinking] = useState(false);

  // Promedios por leg
  const [legAverages, setLegAverages] = useState([]);

  // Refs
  const roundsPanelRef = useRef(null);
  const mobileScrollRefs = useRef([]);
  const scoresDisplayRef = useRef(null);
  const botTimerRef = useRef(null);
  const celebrationTimerRef = useRef(null);
  const resultModalTimerRef = useRef(null);

  // ─── Refs espejo para lectura síncrona en lógica del bot ───
  const botTurnActive = useRef(false);
  const scoresRef = useRef([gameMode, gameMode]);
  const throwsRef = useRef([[], []]);
  const dartsThrownRef = useRef([0, 0]);
  const count180sRef = useRef([0, 0]);
  const advancedStatsRef = useRef(createEmptyMatchAdvancedStats());
  const matchesWonRef = useRef([0, 0]);
  const legAveragesRef = useRef([]);

  // Modals
  const [dardosModal, setDardosModal] = useState({ visible: false, closingScore: 0 });
  const [pendingWin, setPendingWin] = useState(null);
  const [resultModal, setResultModal] = useState(EMPTY_RESULT_MODAL);
  const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', message: '', onConfirm: null });
  const [alertMsg, setAlertMsg] = useState(null);

  // Promedios en tiempo real
  const currentAverages = useMemo(() => {
    return [0, 1].map(i => {
      const totalPoints = throws[i].reduce((sum, t) => sum + t, 0);
      const totalDarts = dartsThrown[i];
      if (totalDarts === 0) return 0;
      return (totalPoints / totalDarts) * 3;
    });
  }, [throws, dartsThrown]);

  const maxRounds = useMemo(() => Math.max(throws[0].length, throws[1].length, 0), [throws]);

  const isMyTurn = activePlayer === 0 && !botThinking && !resultModal.pending;

  // ─── Sincronizar refs con estado ───
  useEffect(() => { scoresRef.current = scores; }, [scores]);
  useEffect(() => { throwsRef.current = throws; }, [throws]);
  useEffect(() => { dartsThrownRef.current = dartsThrown; }, [dartsThrown]);
  useEffect(() => { count180sRef.current = count180s; }, [count180s]);
  useEffect(() => { advancedStatsRef.current = advancedStats; }, [advancedStats]);
  useEffect(() => { matchesWonRef.current = matchesWon; }, [matchesWon]);
  useEffect(() => { legAveragesRef.current = legAverages; }, [legAverages]);

  // Auto-scroll
  useEffect(() => {
    const el = roundsPanelRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    mobileScrollRefs.current.forEach(ref => {
      if (ref) requestAnimationFrame(() => { ref.scrollLeft = ref.scrollWidth; });
    });
  }, [throws]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
    scoresDisplayRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, []);

  // Alert auto-dismiss
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => setAlertMsg(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  const clearCelebrationTimers = useCallback(() => {
    if (celebrationTimerRef.current) {
      clearTimeout(celebrationTimerRef.current);
      celebrationTimerRef.current = null;
    }

    if (resultModalTimerRef.current) {
      clearTimeout(resultModalTimerRef.current);
      resultModalTimerRef.current = null;
    }
  }, []);

  // Cleanup bot timer on unmount
  useEffect(() => {
    return () => {
      clearCelebrationTimers();
      if (botTimerRef.current) clearTimeout(botTimerRef.current);
    };
  }, [clearCelebrationTimers]);

  const triggerScoreCelebration = useCallback((points, didCheckout) => {
    const celebrationType = getScoreCelebrationType(points, didCheckout);

    if (!celebrationType) {
      return;
    }

    if (celebrationTimerRef.current) {
      clearTimeout(celebrationTimerRef.current);
    }

    setScoreCelebration(celebrationType);
    celebrationTimerRef.current = setTimeout(() => {
      setScoreCelebration(null);
      celebrationTimerRef.current = null;
    }, SCORE_CELEBRATION_DURATION_MS);
  }, []);

  const openResultModal = useCallback((data, points) => {
    if (resultModalTimerRef.current) {
      clearTimeout(resultModalTimerRef.current);
      resultModalTimerRef.current = null;
    }

    if (shouldDelayResultModalForCelebration(points)) {
      setResultModal({ visible: false, data, pending: true });
      resultModalTimerRef.current = setTimeout(() => {
        setResultModal({ visible: true, data, pending: false });
        resultModalTimerRef.current = null;
      }, SCORE_CELEBRATION_DURATION_MS);
      return;
    }

    setResultModal({ visible: true, data, pending: false });
  }, []);

  // ─── BOT TURN ───
  // Se dispara cuando activePlayer === 1 y no hay modal abierto.
  // Usa botTurnActive como mutex para evitar ejecuciones múltiples.
  useEffect(() => {
    if (activePlayer !== 1) return;
    if (resultModal.visible || resultModal.pending || dardosModal.visible) return;
    if (botTurnActive.current) return; // ← mutex: evitar re-entrada

    botTurnActive.current = true;
    setBotThinking(true);
    const thinkTime = getBotThinkingTime(botLevel);

    botTimerRef.current = setTimeout(() => {
      // Leer estado actual vía refs (síncrono, sin functional updates)
      const botScore = scoresRef.current[1];
      const { score: botPoints, isCheckout, dartsUsed } = generateBotScore(botLevel, botScore);
      executeBotTurn(botPoints, isCheckout, dartsUsed);
    }, thinkTime);

    return () => {
      if (botTimerRef.current) clearTimeout(botTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayer, botLevel, resultModal.visible, resultModal.pending, dardosModal.visible]);

  // ─── Ejecutar turno del bot ───
  // Lee todo de refs, hace un batch síncrono de setState, libera el mutex.
  const executeBotTurn = useCallback((points, isCheckout, dartsUsed) => {
    const currentScore = scoresRef.current[1];
    setBotThinking(false);

    // BUST
    if (!isValidMove(currentScore, points)) {
      const newThrows = [throwsRef.current[0].slice(), throwsRef.current[1].slice()];
      newThrows[1].push(0);
      const nextAdvancedStats = registerAdvancedTurn(
        advancedStatsRef.current,
        1,
        currentScore,
        0,
        false,
      );
      setAdvancedStats(nextAdvancedStats);
      setThrows(newThrows);
      setDartsThrown([dartsThrownRef.current[0], dartsThrownRef.current[1] + 3]);
      setActivePlayer(0);
      botTurnActive.current = false;
      return;
    }

    const newScore = currentScore - points;
    const didCheckout = newScore === 0;
    const nextAdvancedStats = registerAdvancedTurn(
      advancedStatsRef.current,
      1,
      currentScore,
      points,
      didCheckout,
    );

    // 180
    let new180s = [...count180sRef.current];
    if (points === 180) {
      new180s = [count180sRef.current[0], count180sRef.current[1] + 1];
      setCount180s(new180s);
    }

    setAdvancedStats(nextAdvancedStats);
    triggerScoreCelebration(points, didCheckout);

    // Registrar tirada
    const newThrows = [throwsRef.current[0].slice(), throwsRef.current[1].slice()];
    newThrows[1].push(points);
    setThrows(newThrows);

    // ─── CHECKOUT: bot cerró ───
    if (didCheckout) {
      const closingDarts = generateClosingDarts(botLevel, currentScore, dartsUsed);
      const newDarts = [dartsThrownRef.current[0], dartsThrownRef.current[1] + closingDarts];
      setDartsThrown(newDarts);
      setScores([scoresRef.current[0], 0]);

      // Calcular promedios del leg
      const legAvgs = [0, 1].map(i => {
        const totalPoints = newThrows[i].reduce((sum, t) => sum + t, 0);
        const totalDarts = newDarts[i];
        if (totalDarts === 0) return 0;
        return (totalPoints / totalDarts) * 3;
      });

      const newLegAverages = [...legAveragesRef.current, legAvgs];
      setLegAverages(newLegAverages);

      const newWon = [matchesWonRef.current[0], matchesWonRef.current[1] + 1];
      setMatchesWon(newWon);

      const isMatchOver = newWon[1] >= legsToWin;
      const totalAverages = [0, 1].map(i => {
        const allAvgs = newLegAverages.map(la => la[i]);
        if (allAvgs.length === 0) return 0;
        return allAvgs.reduce((sum, a) => sum + a, 0) / allAvgs.length;
      });

      const playerStats = [config.player1Name, config.player2Name].map((name, index) => ({
        name,
        gamesWon: newWon[index],
        average: legAvgs[index],
        totalAverage: totalAverages[index],
        count180s: new180s[index],
        advancedStats: index === 0 ? nextAdvancedStats.jugador1 : nextAdvancedStats.jugador2,
      }));

      openResultModal({
        winner: 1,
        isMatchOver,
        playerStats,
      }, points);

      botTurnActive.current = false;
      return;
    }

    // ─── Tirada normal ───
    setScores([scoresRef.current[0], newScore]);
    setDartsThrown([dartsThrownRef.current[0], dartsThrownRef.current[1] + 3]);
    setActivePlayer(0);
    botTurnActive.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botLevel, legsToWin, config.player1Name, config.player2Name, openResultModal, triggerScoreCelebration]);

  // ─── TECLADO FÍSICO ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (dardosModal.visible || resultModal.visible || resultModal.pending || confirmModal.visible) return;
      if (!isMyTurn) return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleNumpad(e.key);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleOk();
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleClear();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
      } else if (e.key === 'z' && e.ctrlKey) {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // ========== PLAYER LOGIC ==========

  const handleNumpad = useCallback((digit) => {
    if (resultModal.pending) return;

    setInput(prev => {
      if (prev.length >= 3) return prev;
      return prev + digit;
    });
  }, [resultModal.pending]);

  const handleClear = useCallback(() => {
    if (resultModal.pending) return;

    if (input === '') {
      processPlayerThrow(0);
    } else {
      setInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, resultModal.pending]);

  const handleOk = useCallback(() => {
    if (resultModal.pending || input === '') return;
    const points = parseInt(input, 10);
    processPlayerThrow(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, resultModal.pending]);

  const processPlayerThrow = (points) => {
    if (activePlayer !== 0) return; // Safety: solo player humano
    const currentScore = scores[0];

    if (points < 0 || points > 180) {
      setAlertMsg('Puntuación inválida. Máximo 180.');
      setInput('');
      return;
    }

    if (!isValidMove(currentScore, points)) {
      const newScore = currentScore - points;
      if (newScore < 0) setAlertMsg(`¡BUST! No puedes restar ${points} a ${currentScore}.`);
      else if (newScore === 1) setAlertMsg('¡BUST! No puedes quedarte en 1.');
      setInput('');
      return;
    }

    // Guardar historial
    setMoveHistory(prev => {
      const entry = {
        scores: [...scores],
        activePlayer,
        dartsThrown: [...dartsThrown],
        throws: throws.map(t => [...t]),
        input,
        count180s: [...count180s],
        advancedStats: normalizeMatchAdvancedStats(advancedStatsRef.current),
      };
      const newH = [...prev, entry];
      return newH.length > 5 ? newH.slice(-5) : newH;
    });

    const newScore = currentScore - points;
    const didCheckout = newScore === 0;
    const nextAdvancedStats = registerAdvancedTurn(
      advancedStatsRef.current,
      0,
      currentScore,
      points,
      didCheckout,
    );

    if (points === 180) {
      setCount180s(prev => [prev[0] + 1, prev[1]]);
    }

    setAdvancedStats(nextAdvancedStats);
    triggerScoreCelebration(points, didCheckout);

    const newThrows = throws.map(t => [...t]);
    newThrows[0].push(points);
    setThrows(newThrows);

    if (didCheckout) {
      setScores(prev => [0, prev[1]]);
      if (currentScore > 110) {
        processPlayerWinWithDarts(3, points, newThrows, nextAdvancedStats);
      } else {
        setPendingWin({ points, newThrows, advancedStats: nextAdvancedStats });
        setDardosModal({ visible: true, closingScore: currentScore });
      }
      setInput('');
      return;
    }

    setScores(prev => [newScore, prev[1]]);
    setDartsThrown(prev => [prev[0] + 3, prev[1]]);
    setActivePlayer(1); // Bot's turn
    setInput('');
  };

  const processPlayerWinWithDarts = (closingDarts, points, currentThrows, finalAdvancedStats) => {
    const winnerDarts = [...dartsThrown];
    winnerDarts[0] += closingDarts;
    setDartsThrown(winnerDarts);

    const legAvgs = [0, 1].map(i => {
      const playerThrows = currentThrows[i];
      const totalPoints = playerThrows.reduce((sum, t) => sum + t, 0);
      const totalDarts = i === 0 ? winnerDarts[i] : dartsThrown[i];
      if (totalDarts === 0) return 0;
      return (totalPoints / totalDarts) * 3;
    });

    const newLegAverages = [...legAverages, legAvgs];
    setLegAverages(newLegAverages);

    const newMatchesWon = [...matchesWon];
    newMatchesWon[0]++;
    setMatchesWon(newMatchesWon);

    const totalAverages = [0, 1].map(i => {
      const allAvgs = newLegAverages.map(la => la[i]);
      if (allAvgs.length === 0) return 0;
      return allAvgs.reduce((sum, a) => sum + a, 0) / allAvgs.length;
    });

    const isMatchOver = newMatchesWon[0] >= legsToWin;

    const playerStats = playerNames.map((name, index) => ({
      name,
      gamesWon: newMatchesWon[index],
      average: legAvgs[index],
      totalAverage: totalAverages[index],
      count180s: count180s[index],
      advancedStats: index === 0 ? finalAdvancedStats.jugador1 : finalAdvancedStats.jugador2,
    }));

    openResultModal({
      winner: 0,
      isMatchOver,
      playerStats,
    }, points);

    setDardosModal({ visible: false, closingScore: 0 });
    setPendingWin(null);
  };

  const handleDardosCierre = (numDarts) => {
    if (pendingWin) {
      processPlayerWinWithDarts(numDarts, pendingWin.points, pendingWin.newThrows, pendingWin.advancedStats);
    }
  };

  const handleNextLeg = () => {
    clearCelebrationTimers();
    setScoreCelebration(null);
    setResultModal(EMPTY_RESULT_MODAL);
    const nextMatchCount = matchCount + 1;
    setMatchCount(nextMatchCount);

    setScores([gameMode, gameMode]);
    setDartsThrown([0, 0]);
    setThrows([[], []]);
    setInput('');
    setMoveHistory([]);
    botTurnActive.current = false;

    const nextStarter = (firstStarter + nextMatchCount) % 2;
    setActivePlayer(nextStarter);
  };

  const handleRematch = () => {
    clearCelebrationTimers();
    setScoreCelebration(null);
    setResultModal(EMPTY_RESULT_MODAL);
    setScores([gameMode, gameMode]);
    setMatchesWon([0, 0]);
    setDartsThrown([0, 0]);
    setThrows([[], []]);
    setCount180s([0, 0]);
    setAdvancedStats(createEmptyMatchAdvancedStats());
    setLegAverages([]);
    setInput('');
    setMoveHistory([]);
    setMatchCount(0);
    setActivePlayer(0);
    setFirstStarter(0);
    setBotThinking(false);
    botTurnActive.current = false;
    if (botTimerRef.current) clearTimeout(botTimerRef.current);
  };

  const handleUndo = () => {
    if (moveHistory.length === 0 || activePlayer !== 0 || resultModal.pending) return;

    setConfirmModal({
      visible: true,
      title: 'Deshacer',
      message: '¿Deshacer la última tirada?',
      onConfirm: () => {
        const lastState = moveHistory[moveHistory.length - 1];
        setScores(lastState.scores);
        setActivePlayer(lastState.activePlayer);
        setDartsThrown(lastState.dartsThrown);
        setThrows(lastState.throws);
        setCount180s(lastState.count180s);
        setAdvancedStats(lastState.advancedStats);
        setInput('');
        setMoveHistory(prev => prev.slice(0, -1));
        setConfirmModal({ visible: false, title: '', message: '', onConfirm: null });
        setBotThinking(false);
        botTurnActive.current = false;
        if (botTimerRef.current) clearTimeout(botTimerRef.current);
      }
    });
  };

  const handleExit = () => {
    setConfirmModal({
      visible: true,
      title: 'Salir de la partida',
      message: '¿Seguro que quieres salir? Se perderá el progreso.',
      onConfirm: () => {
        clearCelebrationTimers();
        if (botTimerRef.current) clearTimeout(botTimerRef.current);
        onBack();
      }
    });
  };

  // Checkout suggestion
  const checkoutSuggestion = useMemo(() => {
    if (!isMyTurn || input !== '') return null;
    const myScore = scores[0];
    if (myScore > 170 || myScore < 2) return null;
    const options = getCheckoutOptions(myScore);
    if (options.length === 0) return null;
    return formatCheckout(options[0]);
  }, [isMyTurn, scores, input]);

  // ========== RENDER ==========

  const numpadKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [input.length > 0 ? '←' : 'BUST', '0', 'OK'],
  ];

  return (
    <div className="x01-game-container">
      {/* Alert */}
      {alertMsg && (
        <div className="x01-alert"><span>{alertMsg}</span></div>
      )}

      {/* Header */}
      <div className="x01-game-header">
        <button className="x01-back-btn" onClick={handleExit}>← Salir</button>
        <div className="x01-match-info">
          <span className="x01-match-mode">{gameMode}</span>
          <span className="x01-match-legs">Primero a {legsToWin}</span>
          <span className="bot-game-level-badge">{botProfile.emoji} {botProfile.name}</span>
        </div>
      </div>

      {/* Legs display */}
      <div className="x01-legs-display">
        {playerNames.map((name, i) => (
          <div key={i} className={`x01-leg-counter ${i === activePlayer ? 'active' : ''} ${i === 0 ? 'is-me' : 'is-bot'}`}>
            <span className="x01-leg-name">
              {name} {i === 0 && <span className="bot-game-you-badge">(Tú)</span>}
              {i === 1 && <span className="bot-game-bot-icon">🤖</span>}
            </span>
            <span className="x01-leg-count">{matchesWon[i]}</span>
          </div>
        ))}
      </div>

      {/* Game Body */}
      <div className="x01-game-body">
        {/* Rounds panel */}
        <div className="x01-rounds-panel">
          <div className="x01-rounds-desktop">
            <div className="x01-rounds-header">
              <span className="x01-rounds-label">#</span>
              {playerNames.map((name, i) => (
                <span key={i} className={`x01-rounds-player ${i === activePlayer ? 'active' : ''}`}>{name}</span>
              ))}
            </div>
            <div className="x01-rounds-body" ref={roundsPanelRef}>
              {maxRounds > 0 ? (
                Array.from({ length: maxRounds }, (_, roundIdx) => (
                  <div key={roundIdx} className={`x01-round-row ${roundIdx === maxRounds - 1 ? 'latest' : ''}`}>
                    <span className="x01-round-num">{roundIdx + 1}</span>
                    {throws.map((playerThrows, pi) => {
                      const val = playerThrows[roundIdx];
                      const cls = val === 180 ? 'one-eighty' : val >= 100 ? 'ton' : val === 0 ? 'bust' : '';
                      return (
                        <span key={pi} className={`x01-round-score ${cls}`}>
                          {val !== undefined ? (val === 0 ? 'BUST' : val) : ''}
                        </span>
                      );
                    })}
                  </div>
                ))
              ) : (
                <p className="x01-rounds-empty">Sin rondas</p>
              )}
            </div>
          </div>

          <div className="x01-rounds-mobile">
            {playerNames.map((name, pi) => (
              <div key={pi} className="x01-rounds-player-row">
                <div className={`x01-rounds-player-label ${pi === activePlayer ? 'active' : ''}`}>
                  <span>{name}</span>
                </div>
                <div className="x01-rounds-player-scroll" ref={el => { mobileScrollRefs.current[pi] = el; }}>
                  {throws[pi].map((pts, idx) => {
                    const isLast = idx === throws[pi].length - 1;
                    const cls = pts === 180 ? 'one-eighty' : pts >= 100 ? 'ton' : pts === 0 ? 'bust' : '';
                    return (
                      <span key={idx} className={`x01-rounds-pill ${cls} ${isLast ? 'latest' : ''}`}>
                        R{idx + 1}.{pts === 0 ? 'BUST' : pts}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center panel: Scores */}
        <div className="x01-center-panel">
          <ScoreCelebrationBanner type={scoreCelebration} />
          <div className="x01-scores-display" ref={scoresDisplayRef}>
            {playerNames.map((name, i) => (
              <div key={i} className={`x01-score-panel ${i === activePlayer ? 'active' : ''}`}>
                <div className="x01-score-number">{scores[i]}</div>
                <div className="x01-score-avg">AVG: {currentAverages[i].toFixed(1)}</div>
              </div>
            ))}
          </div>

          {/* Input / Bot thinking */}
          <div className="x01-input-section">
            {activePlayer === 1 || botThinking ? (
              <div className="bot-thinking-indicator">
                <div className="bot-thinking-dots">
                  <span></span><span></span><span></span>
                </div>
                <p>🤖 {player2Name} está pensando...</p>
              </div>
            ) : (
              <>
                <div className="x01-input-display">
                  {input === '' && checkoutSuggestion ? (
                    <span className="x01-checkout-hint-big">{checkoutSuggestion}</span>
                  ) : (
                    <span className="x01-input-value">{input || '\u2014'}</span>
                  )}
                </div>
                <button
                  className={`x01-undo-btn ${moveHistory.length === 0 || resultModal.pending ? 'disabled' : ''}`}
                  onClick={handleUndo}
                  disabled={moveHistory.length === 0 || resultModal.pending}
                >
                  ↩ Deshacer
                </button>
              </>
            )}
          </div>
        </div>

        {/* Numpad */}
        <div className="x01-right-panel">
          <div className={`x01-numpad ${!isMyTurn || resultModal.pending ? 'disabled' : ''}`}>
            {numpadKeys.map((row, ri) => (
              <div key={ri} className="x01-numpad-row">
                {row.map(key => {
                  const isBack = key === '←';
                  const isBust = key === 'BUST';
                  const isOK = key === 'OK';
                  let cls = 'x01-numpad-key';
                  if (isBack || isBust) cls += ' clear';
                  if (isOK) cls += ' ok';
                  return (
                    <button
                      key={key}
                      className={cls}
                      disabled={!isMyTurn || resultModal.pending}
                      onClick={() => {
                        if (!isMyTurn || resultModal.pending) return;
                        if (isOK) handleOk();
                        else if (isBack) setInput(prev => prev.slice(0, -1));
                        else if (isBust) handleClear();
                        else handleNumpad(key);
                      }}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <SponsorsStrip />

      <div className="x01-keyboard-hint">
        Teclado: 0-9 · Enter = OK · Backspace = Borrar · Ctrl+Z = Deshacer
      </div>

      {/* Modals */}
      <DardosCierreModal
        visible={dardosModal.visible}
        closingScore={dardosModal.closingScore}
        onSelect={handleDardosCierre}
      />

      <TrainingMatchResultModal
        visible={resultModal.visible}
        data={resultModal.data}
        onNextLeg={handleNextLeg}
        onFinish={onBack}
        onRematch={handleRematch}
      />

      <ConfirmModal
        visible={confirmModal.visible}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ visible: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

export default BotGame;
